import dayjs, { type Dayjs } from "dayjs";

import type { EstimatorSources } from "../../_context/EstimatorSourcesContext";
import { planSearch, type SearchResult } from "../flight-search";
import type { FaresSnapshot } from "../saved/ui-types";
import type { HotelRate, Workspace } from "../types";
import { cityKey, createWorkspace } from "../workspace";
import {
  SAMPLE_LEAD_DAYS,
  SAMPLE_NIGHTS,
  SAMPLE_START_WEEKDAY,
  SAMPLE_TITLE,
  SAMPLE_VENUE_COUNT_FLOOR,
} from "../constants";

/** A home city in the sample, with the airport it flies from. */
interface SampleHome {
  city: string;
  airport: string;
  airportName: string;
  count: number;
}

/** A destination in the sample: its airport, region and typical venue nightly rate. */
interface SampleDestination {
  city: string;
  airport: string;
  airportName: string;
  region: string;
  perNight: { low: number; high: number };
}

/** One sample fare: home airport to destination airport, per traveler in USD. */
type SampleFare = [from: string, to: string, usd: number, minutes: number, airline: string, flight: string];

const HOMES: readonly SampleHome[] = [
  { city: "San Francisco", airport: "SFO", airportName: "San Francisco International", count: 10 },
  { city: "New York", airport: "JFK", airportName: "John F. Kennedy International", count: 8 },
  { city: "Chicago", airport: "ORD", airportName: "O'Hare International", count: 5 },
];

const DESTINATIONS: readonly SampleDestination[] = [
  {
    city: "Denver",
    airport: "DEN",
    airportName: "Denver International",
    region: "US-CO",
    perNight: { low: 179, high: 249 },
  },
  {
    city: "Austin",
    airport: "AUS",
    airportName: "Austin-Bergstrom International",
    region: "US-TX",
    perNight: { low: 199, high: 289 },
  },
  {
    city: "Nashville",
    airport: "BNA",
    airportName: "Nashville International",
    region: "US-TN",
    perNight: { low: 219, high: 309 },
  },
];

const FARES: readonly SampleFare[] = [
  ["SFO", "DEN", 289, 155, "United Airlines", "UA 1712"],
  ["SFO", "AUS", 342, 220, "United Airlines", "UA 2216"],
  ["SFO", "BNA", 398, 255, "Southwest Airlines", "WN 1873"],
  ["JFK", "DEN", 318, 265, "Delta Air Lines", "DL 1543"],
  ["JFK", "AUS", 296, 260, "JetBlue", "B6 1263"],
  ["JFK", "BNA", 248, 165, "Delta Air Lines", "DL 2108"],
  ["ORD", "DEN", 198, 165, "United Airlines", "UA 657"],
  ["ORD", "AUS", 236, 175, "American Airlines", "AA 1388"],
  ["ORD", "BNA", 162, 105, "Southwest Airlines", "WN 2011"],
];

/** The sample as the estimator opens it, with the hotel rates it was priced with. */
export interface SampleEstimate {
  workspace: Workspace;
  fares: FaresSnapshot;
  faresCheckedAt: string;
  /** Venue hotel rates keyed by {@link cityKey}. */
  hotels: Readonly<Record<string, HotelRate>>;
}

/**
 * The trip's dates: a few weeks out, starting on the same weekday, so the sample never looks
 * stale and its fares are always inside the airlines' publish window.
 * @param today - Today.
 * @returns Start and end as `YYYY-MM-DD`.
 * @throws Never.
 */
const sampleDates = (today: Dayjs): { start: string; end: string } => {
  const earliest = today.startOf("day").add(SAMPLE_LEAD_DAYS, "day");
  const start = earliest.add((SAMPLE_START_WEEKDAY - earliest.day() + 7) % 7, "day");
  return { start: start.format("YYYY-MM-DD"), end: start.add(SAMPLE_NIGHTS, "day").format("YYYY-MM-DD") };
};

/**
 * A priced sample estimate for the public page: 23 people from San Francisco, New York and
 * Chicago comparing Denver, Austin and Nashville. Its fares are a finished search for the
 * workspace's own plan, so the estimator shows them without searching.
 * @param today - Today, so the dates sit ahead of it.
 * @returns The sample workspace, fares and hotel rates.
 * @throws When the sample no longer makes a search plan (a fixture bug, caught by its tests).
 */
export const buildSampleEstimate = (today: Dayjs = dayjs()): SampleEstimate => {
  const all = [...HOMES, ...DESTINATIONS];
  const workspace: Workspace = {
    ...createWorkspace("USD"),
    title: SAMPLE_TITLE,
    origins: HOMES.map(({ city, count }) => ({ city, count })),
    headcount: HOMES.reduce((sum, home) => sum + home.count, 0),
    destinations: DESTINATIONS.map(({ city }) => city),
    dates: sampleDates(today),
    airports: Object.fromEntries(all.map(({ city, airport }) => [cityKey(city), airport])),
    places: Object.fromEntries(DESTINATIONS.map(({ city, region }) => [cityKey(city), region])),
  };
  const planned = planSearch(workspace);
  if (!planned) throw new Error("The sample estimate has no search plan");

  const groupOf = new Map(HOMES.map((home) => [home.airport, home.city]));
  const results = FARES.map(
    ([from, to, usd, minutes, airline, flightNumber]): SearchResult => ({
      groupName: groupOf.get(from) ?? from,
      departureCode: from,
      arrivalCode: to,
      travelClass: workspace.cabin,
      error: null,
      flight: {
        price: usd,
        minutes,
        layovers: 0,
        layoverMinutes: 0,
        airline,
        flightNumber,
        airlineLogo: null,
        carbonKg: Math.round(minutes * 1.6),
        url: null,
        details: null,
      },
    })
  );
  const fares: FaresSnapshot = {
    signature: planned.signature,
    entry: {
      plan: planned.plan,
      started: { token: null, airports: Object.fromEntries(all.map(({ airport }) => [cityKey(airport), airport])) },
      poll: {
        status: "complete",
        progress: 1,
        airportNames: Object.fromEntries(all.map(({ airport, airportName }) => [airport, airportName])),
        results,
      },
      error: null,
    },
  };
  const hotels = Object.fromEntries(
    DESTINATIONS.map(({ city, perNight }, index): [string, HotelRate] => [
      cityKey(city),
      {
        status: "venues",
        perNight,
        venueCount: SAMPLE_VENUE_COUNT_FLOOR + index * 9,
        updatedAt: today.toISOString(),
      },
    ])
  );
  return { workspace, fares, faresCheckedAt: today.toISOString(), hotels };
};

/**
 * Sources that answer from the sample and never call an action: its hotel rates, and no org
 * place history.
 * @param hotels - The sample's hotel rates, keyed by {@link cityKey}.
 * @returns Sources for {@link EstimatorSources}.
 * @throws Never.
 */
export const sampleSources = (hotels: Readonly<Record<string, HotelRate>>): EstimatorSources => ({
  hotelRate: async (city) => hotels[cityKey(city)] ?? { status: "none" },
  pastDestinations: async () => [],
  frequentHomeCities: async () => [],
});
